import get from 'lodash.get';

import {
  ALBUM_IDENTITY_ACCESS,
  IDENTITY_RELATIONSHIP,
  IDENTITY_SCHOOL_GRADES,
} from '../constants/identity';
import { PPGD_JOIN_TYPE } from '../constants/discover-album-folder';
import { ALBUM_FOLDER_TAG_TYPE } from '../constants/album-folder-tags';
import { VOTE_DECISION } from '../constants/vote-decisions';
import { ACCOUNT_TYPE } from '../apps/pro/views/AlbumFolder/AlbumFolders/AlbumFolderMembers/components/AlbumFolderIdentityMembershipDetails/constants';
/**
 * Determines if an identity is of type Roster Identity.
 *
 * @param {Object} identity The identity to check.
 * @returns {undefined|boolean}
 */
const isRosterIdentity = identity => {
  return identity.__typename === 'RosterIdentity';
};

/**
 * Determines if an identity is of PRIMARY relationship or not.
 *
 * @param {Object} identity The identity to check.
 * @returns {undefined|boolean}
 */
const isPrimaryIdentity = identity => {
  return identity && identity.relationship === IDENTITY_RELATIONSHIP.PRIMARY;
};

/**
 * Given a list of identities, attempts to sort
 * by the relationship they have. At the moment,
 * this would place all primary identities (1 by norm)
 * at the top.
 *
 * @param {Array<Identities>} identities
 */
const sortByIdentityRelationship = identities => {
  return [...identities].sort((a, b) => {
    const aIsPrimary = get(a, 'relationship') === IDENTITY_RELATIONSHIP.PRIMARY;
    const bIsPrimary = get(b, 'relationship') === IDENTITY_RELATIONSHIP.PRIMARY;

    if (aIsPrimary === bIsPrimary) {
      return 0;
    }

    if (aIsPrimary && !bIsPrimary) {
      return -1;
    }

    return 1;
  });
};

/**
 * Determines if an identity has a selfie, checking redux recently uploaded
 * photos as well.
 *
 * @param {Object} identity The identity to check.
 * @param {Array} [storePhotos] Redux photos from photoUpload reducer.
 * @returns {boolean}
 */
const identityHasSelfie = (identity, storePhotos = []) => {
  const hasStorePhoto = storePhotos.find(
    photo => identity.id === photo.identityId,
  );
  const hasExistingPhoto = identity.current_photo != null;

  return hasExistingPhoto || !!hasStorePhoto;
};

/**
 * Returns selected identities with albums and
 * remaining identities (filtered out the above) with selfies.
 *
 * @param {Array} selectedIdentities Selected identites from onboarding process.
 * @param {Array} identities All account identities.
 * @param {Array} [storePhotos] Recent photo uploads from redux store.
 * @returns {{selectedIdsWithAlbums, remainingIdentities}}
 */
const filteredIdentityData = (selectedIdentities, identities, storePhotos) => {
  // Get only IDs that actually have albums selected
  const selectedIdsWithAlbums = selectedIdentities.filter(
    ({ selectedAlbums }) => selectedAlbums.length,
  );

  // From all identities, filter out all selected identities and
  // identities without a selfie (even in redux store)
  const remainingIdentities = identities.filter(
    identity =>
      !selectedIdsWithAlbums.find(
        selectedId => selectedId.id === identity.id,
      ) && identityHasSelfie(identity, storePhotos),
  );

  return {
    selectedIdsWithAlbums,
    remainingIdentities,
  };
};

/**
 * Gets an album with its SelectedIdentities and returns
 * an array of all SelectedIdentities' IDs.
 *
 * @param {Object} album - The album.
 * @param {Object} album.selected_identities - The album's selected identities.
 * @param {Array.<Object>} album.selected_identities.records - The selected identity records.
 * @returns {Array.<String>}
 */
const getAlbumSelectedIdentitiesIds = album => {
  if (!album) {
    return [];
  }

  return album.selected_identities.records.map(({ id }) => id);
};

/**
 * Determines if a certain identity is purchased on the given album or not
 *
 * @param {Array<Object>} identity - The identity as returned from GQL.
 * @param {string} albumId - The album ID to check.
 * @returns {boolean} - Whether the identity is purchased or not.
 */
const isIdentityPurchasedOnAlbum = (identity, albumId) => {
  if (!identity || !identity.album_memberships || !albumId) {
    return false;
  }

  const albumMembership = (identity?.album_memberships?.records ?? []).find(
    membership => membership.album.id === albumId,
  );

  if (!albumMembership) {
    return false;
  }

  // Define a "Purchased" identity as either, one which has been
  // directly purchased, or one which does not need to be (no sub possible)
  return (
    !!(albumMembership.purchased_identities?.records ?? []).find(
      ({ id }) => id === identity.id,
    ) || !albumMembership.album?.price_whole_album?.value
  );
};

/**
 * Returns an array of joined album IDs for the provided identity.
 *
 * @param {Array.<Object>} identity - The identity as returned from GQL.
 * @param {Boolean} excludePurchasable - whether or not to include purchasable albums regardless of join status
 * @returns {Array} - The album IDs.
 */
const getIdentityJoinedAlbumIds = (identity, excludePurchasable = false) => {
  if (!identity.album_memberships) {
    return [];
  }

  if (excludePurchasable) {
    return identity.album_memberships.records
      .filter(({ album }) => !isIdentityPurchasedOnAlbum(identity, album.id))
      .map(({ album }) => album.id);
  }

  return identity.album_memberships.records.map(({ album }) => album.id);
};

/**
 * Determines if a certain identity is joined to an album or not.
 *
 * @param {Array<Object>} identity - The identity as returned from GQL.
 * @param {string} albumId - The album ID to check.
 * @returns {boolean} - Whether the identity is joined or not.
 */
const isIdentityJoinedToAlbum = (identity, albumId) => {
  if (!identity || !albumId) {
    return false;
  }

  const joinedAlbumsIds = getIdentityJoinedAlbumIds(identity);
  return joinedAlbumsIds.includes(albumId);
};

/**
 * Given an identity with its album memberships and an album id
 * it returns whether the identity has matches based on album_membership.matched_photos_extended for the given album.
 *
 * @param {Object} identity - The identity.
 * @param {string} albumId - The album id.
 * @returns {boolean}
 */
const identityHasAlbumMatches = (identity, albumId) => {
  if (!identity || !identity.album_memberships || !albumId) {
    return false;
  }

  const albumMembership = identity.album_memberships.records.find(
    membership => membership.album.id === albumId,
  );

  return get(albumMembership, 'matched_photos_extended.total', 0) > 0;
};

/**
 * Resolves an identity's name.
 *
 * Since Roster Identities are account-tied, if a roster_identity is found,
 * that information will be used over the real identity's name.
 *
 * `identity.account_identity_name` is an alias and refers to how the given
 * `Identity` is supposed to be named for a specific account that provided
 * the alias itself.
 *
 * For example, a Camp Admin may choose to rename any identity that's joined
 * to their albums in their album member dashboard. Let's say
 * that the Camp Admin gives an alias "George" to an identity named "John".
 * _Only_ the Camp Admin is able to see that identity named as "George"
 * (as it's an account-specific alias), the consumer will continue
 * to see their name as "John".
 *
 * @param {Object} identity - The identity.
 * @param {Object} [account] - The user account.
 * @returns {string} - The identity name to be displayed.
 */
const getIdentityName = (identity, account = {}) => {
  if (!identity) {
    return '';
  }

  const { roster_identity: rosterIdentity } = identity;

  if (rosterIdentity?.first_name) {
    return rosterIdentity.first_name;
  }

  if (isRosterIdentity(identity)) {
    return identity.first_name;
  }

  const accountIdentityName = identity.account_identity_name;

  if (accountIdentityName && accountIdentityName.name) {
    return accountIdentityName.name;
  }

  if (
    isPrimaryIdentity(identity) &&
    identity.name &&
    identity.name.toLowerCase() === 'Me'.toLowerCase() &&
    account.name
  ) {
    return account.name;
  }

  return get(identity, 'name') || get(account, 'name') || 'No name';
};

/**
 * Returns an identity's last name.
 *
 * Since Roster Identities are account-tied, if a roster_identity is found,
 * that information will be used over the real identity's name.
 *
 * @param {Object} identity - The identity.
 * @returns {string} - The identity's last name.
 */
const getIdentityLastName = identity => {
  if (!identity) {
    return '';
  }

  const { roster_identity: rosterIdentity } = identity;

  if (rosterIdentity?.last_name) {
    return rosterIdentity.last_name;
  }

  const accountIdentityName = identity.account_identity_name;

  if (accountIdentityName?.last_name) {
    return accountIdentityName.last_name;
  }

  return identity.last_name || '';
};

/**
 * Returns an identity's full name.
 *
 * @param {Object} identity - The identity.
 * @param {Object} [account] - The account.
 * @returns {string} - The identity's full name.
 */
const getIdentityFullName = (identity, account) => {
  if (!identity) {
    return '';
  }

  return `${getIdentityName(identity, account)} ${getIdentityLastName(
    identity,
  )}`.trim();
};

/**
 * Filters identities based on ALBUM_IDENTITY_ACCESS.
 *
 * @param {Array} identities - The list of identities.
 * @param {ALBUM_IDENTITY_ACCESS} access - The level of access.
 * @returns {Array}
 */
const identitiesFilteredOnAccess = (identities, access) => {
  if (!identities) {
    return [];
  }

  if (access === ALBUM_IDENTITY_ACCESS.PRIMARY_ONLY) {
    return identities.filter(
      ({ relationship }) => relationship === IDENTITY_RELATIONSHIP.PRIMARY,
    );
  }

  if (access === ALBUM_IDENTITY_ACCESS.SECONDARY_ONLY) {
    return identities.filter(
      ({ relationship }) => relationship === IDENTITY_RELATIONSHIP.SECONDARY,
    );
  }

  return identities;
};

/**
 * Determines if an identity is joined to any albums that are children of a
 * given root album folder.
 *
 * The `identity` passed in must have its album_membership.album records
 * and these albums must have a reference to their root folder.
 *
 * @param {object} identity - The identity.
 * @param {string} rootAlbumFolderId - The id of the root album folder to examine.
 * @returns {boolean} - Whether the identity is joined to albums of the passed in root album folder.
 */
const identityJoinedToRootAlbumFolder = (identity, rootAlbumFolderId) => {
  if (!identity || !rootAlbumFolderId) {
    return false;
  }

  const joinedAlbums = get(identity, 'album_memberships.records', []).map(
    membership => get(membership, 'album'),
  );
  const albumFolderIds = joinedAlbums.map(album =>
    get(album, 'root_album_folder.id'),
  );

  return albumFolderIds.includes(rootAlbumFolderId);
};

/**
 * Whether a user can complete checkout (on any checkout, either album folder,
 * or individual album) without a phone number.
 *
 * @param {Object} user - The user's account object.
 * @param {string} user.phone_number - The user's phone number.
 * @param {Array<Object>} selectedIdentities - The user's selected identities for checkout.
 * @returns {boolean}
 */
const requiresValidPhoneToCompleteCheckout = (
  user,
  selectedIdentities = [],
) => {
  if (!user || !selectedIdentities || !selectedIdentities.length) {
    return false;
  }

  const hasPpd = selectedIdentities.some(
    selectedIdentity => selectedIdentity.joinType === PPGD_JOIN_TYPE.JOIN,
  );

  return !user.phone_number && hasPpd;
};

/**
 * Returns the most relevant identity for PPD purchase based on the number of matched photos.
 *
 * @param {Array<Object>} selectedIdentities - The user's selected identities for purchase.
 * @returns {Object|undefined} - The identity with the most matches, or the first identity if all have equal matches.
 */
const getInitialActiveIdentityForPpdPurchase = (selectedIdentities = []) => {
  if (!selectedIdentities || !selectedIdentities.length) {
    return undefined;
  }

  return selectedIdentities.reduce((max, selectedIdentity) => {
    if (
      get(selectedIdentity, 'matched_photos_extended.total', 0) >
      get(max, 'matched_photos_extended.total', 0)
    ) {
      return selectedIdentity;
    }

    return max;
  }, selectedIdentities[0]);
};

/**
 * Given an identity it constructs a heuristic based on its first and last names.
 *
 * @param {Object} identity - The identity.
 * @param {string} identity.name - The identity's first name.
 * @param {string} identity.last_name - The identity's last name.
 * @returns {string} - The heuristic.
 */
const getIdentityNameHeuristic = identity => {
  if (!identity) {
    return '';
  }

  const name = identity.name || '';
  const lastName = identity.last_name || '';

  return `${name.trim().toLowerCase()} ${lastName.trim().toLowerCase()}`;
};

/**
 * Given an array of identities it returns an array of their name heuristics.
 *
 * @param {Array<Object>} identities - The identities.
 * @returns {Array<string>}
 */
const getAccountIdentityNamesArray = identities => {
  if (!identities) {
    return [];
  }

  return identities.map(getIdentityNameHeuristic);
};

/**
 *  Returns a list of associated accounts to an identity or roter identity.
 *
 * @param {Identity} identity Identity to get accounts from.
 * @returns {Array<Account>} List of associated accounts.
 *  */
const getIdentityAccounts = identity => {
  if (!identity) {
    return [];
  }

  return isRosterIdentity(identity)
    ? get(identity, 'linked_identities.records', []).map(linkedIdentity => ({
        ...(linkedIdentity?.account ?? {}),
        phone_number: linkedIdentity.phone_number,
      }))
    : [identity.account];
};

/**
 * Returns the jersey & uniform information of an identity based on its tags & album membership.
 *
 * @param {Object} selectedIdentity Selected Identity to get jersey info from.
 */
const getJerseyInfo = selectedIdentity => {
  const albumTags = get(
    selectedIdentity,
    'album_membership.album.album_folder_tags.records',
    [],
  );
  const identityTags = get(selectedIdentity, 'album_folder_tags.records', []);

  /**
   * Filtered Active Identity tags per the selected identity album membership.
   *
   * @typedef {Object} ActiveIdentityAlbumTag
   * @property {UniformAlbumFolderTagJersey} jersey Relevat jersey for the album/selectedIdentity.
   * @property {UniformAlbumFolderTagLevel} level Relevant level for the album/selectedIdentity.
   * @property {IdentityAlbumAlbumFolderTag} identityTag The actual identity tag.
   */

  /**
   * @type {ActiveIdentityAlbumTag}
   */
  const activeIdentityAlbumTags = identityTags
    .map(identityTag => {
      if (identityTag.album_folder_tag.type !== ALBUM_FOLDER_TAG_TYPE.UNIFORM) {
        return undefined;
      }

      const albumTag = albumTags.find(
        ({ album_folder_tag: albumFolderTag }) =>
          albumFolderTag.id === identityTag.album_folder_tag.id,
      );

      if (!albumTag) {
        return undefined;
      }

      const albumUniformAlbumFolderLevel = get(
        albumTag,
        'uniform_album_folder_tag_levels.records',
        [],
      ).find(
        albumUniformAlbumFolderLevelRecord =>
          albumUniformAlbumFolderLevelRecord?.uniform_album_folder_tag_level
            ?.id === identityTag?.uniform_album_folder_tag_level?.id ?? 'N/A',
      );

      if (!albumUniformAlbumFolderLevel) {
        return undefined;
      }

      // Cross reference the album jerseys with the
      // team the identity has selected. 2 possible scenarios:
      // 1. A jersey for that team is set & found, which will display the identity jersey color info.
      // 2. A jersey for that team is NOT found (i.e, the team is not playing in this album), and as such, N/A will be displayed.
      const identityJersey = (
        albumUniformAlbumFolderLevel?.uniform_album_folder_tag_level_jerseys
          ?.records ?? []
      ).find(
        jersey =>
          jersey.team?.id ===
          identityTag?.uniform_album_folder_tag_level_team?.id,
      );

      return {
        level: albumUniformAlbumFolderLevel?.uniform_album_folder_tag_level,
        jersey: identityJersey,
        identityTag,
      };
    })
    .filter(item => !!item);

  const [activeIdentityAlbumTag] = activeIdentityAlbumTags;

  return {
    jersey: activeIdentityAlbumTag?.jersey,
    identityUniformTag: activeIdentityAlbumTag?.identityTag,
  };
};

/**
 * Determines if an identity is a cloned identity (not a master identity).
 *
 * @param {Object} selectedIdentity - The identity to check.
 * @returns {boolean} - Whether the identity is a cloned identity.
 */
const isClonedIdentity = selectedIdentity => {
  return (
    (selectedIdentity?.selected_identity_link?.is_master !== undefined &&
      !selectedIdentity.selected_identity_link.is_master) ??
    false
  );
};

/**
 * Returns an array of reference photos for an identity, including selfies and matched vote photos.
 *
 * @param {Object} identity - The identity to get references for.
 * @returns {Array<Object>} - Array of reference photos with metadata.
 */
const getIdentityReferences = identity => {
  const identitySelfies = (identity?.selfies?.records ?? []).map(selfie => ({
    ...selfie.photo,
    facebox: selfie?.face?.facebox,
    type: selfie?.selfie_type,
  }));

  const matchedVotePhotos = (identity?.face_match_votes?.records ?? [])
    .filter(vote => vote.vote === VOTE_DECISION.TOTALLY)
    .map(vote => ({
      ...vote.compared_face.photo,
      facebox: vote.compared_face.facebox,
      history: vote.history,
    }));

  return [...identitySelfies, ...matchedVotePhotos];
};

/**
 * Returns the main linked identity for a roster identity, prioritizing those with photos
 * and non-marketing accounts.
 *
 * @param {Object} rosterIdentity - The roster identity to process.
 * @returns {Object|null} - The main linked identity or null if not found.
 */
const getRosterIdentityMainLinkedIdentity = rosterIdentity => {
  if (!rosterIdentity) {
    return null;
  }

  return (
    (rosterIdentity.linked_identities?.records ?? []).find(
      linkedIdentity => !!linkedIdentity?.identity?.current_photo,
    ) ||
    (rosterIdentity.linked_identities?.records ?? []).find(
      linkedIdentity =>
        linkedIdentity?.account?.type !== ACCOUNT_TYPE.MARKETING,
    ) ||
    rosterIdentity.linked_identities?.records?.[0]
  );
};

/**
 * Returns the selfie photo for a roster identity, looking through various possible sources.
 *
 * @param {Object} rosterIdentity - The roster identity to get the selfie photo for.
 * @returns {Object|null} - The selfie photo or null if not found.
 */
const getRosterIdentitySelfiePhoto = rosterIdentity => {
  if (!rosterIdentity) {
    return null;
  }

  const mainLinkedIdentity =
    getRosterIdentityMainLinkedIdentity(rosterIdentity);

  return (
    rosterIdentity.selfie_photo ??
    rosterIdentity.selfie_photo_urls?.records?.[0]?.selfie_photo ??
    rosterIdentity.selfie_photo_filenames?.records?.[0]?.selfie_photo ??
    mainLinkedIdentity?.identity?.current_photo ??
    null
  );
};

/**
 * Returns the address for a roster identity from its main linked identity.
 *
 * @param {Object} rosterIdentity - The roster identity to get the address for.
 * @returns {Object|null} - The address or null if not found.
 */
const getRosterIdentityAddress = rosterIdentity => {
  if (!rosterIdentity) {
    return null;
  }

  const mainLinkedIdentity =
    getRosterIdentityMainLinkedIdentity(rosterIdentity);

  return mainLinkedIdentity?.addresses?.records?.[0];
};

/**
 * Returns the appropriate link record for a roster identity based on the current context.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.rosterIdentity - The roster identity to get the link for.
 * @param {string} params.albumId - The album ID to match against (optional).
 * @param {string} params.albumFolderId - The album folder ID to match against (optional).
 * @returns {Object|null} - The matching link record or null if not found.
 */
const getRosterIdentityLink = ({ rosterIdentity, albumId, albumFolderId }) => {
  if (!rosterIdentity?.links?.records) {
    return null;
  }

  return (
    rosterIdentity.links.records.find(link => {
      if (albumId && link.album?.id === albumId) {
        return true;
      }

      if (albumFolderId && link.album_folder?.id === albumFolderId) {
        return true;
      }

      return false;
    }) ?? null
  );
};

/**
 * Returns the teacher name for a roster identity from link records.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.rosterIdentity - The roster identity to get the teacher for.
 * @param {string} params.albumId - The album ID to match against (optional).
 * @param {string} params.albumFolderId - The album folder ID to match against (optional).
 * @returns {string|null} - The teacher name or null if not available.
 */
const getRosterIdentityTeacher = ({
  rosterIdentity,
  albumId,
  albumFolderId,
}) => {
  if (!rosterIdentity) {
    return null;
  }

  const link = getRosterIdentityLink({
    rosterIdentity,
    albumId,
    albumFolderId,
  });
  return link?.teacher_name || rosterIdentity?.teacher_name;
};

/**
 * Returns the formatted grade for a roster identity from link records, removing the word "grade" if present.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.rosterIdentity - The roster identity to get the grade for.
 * @param {string} params.albumId - The album ID to match against (optional).
 * @param {string} params.albumFolderId - The album folder ID to match against (optional).
 * @param {boolean} params.rawValue - Wether or not to return the raw value
 * @returns {string|null} - The formatted grade or null if not available.
 */
const getRosterIdentityGrade = ({
  rosterIdentity,
  albumId,
  albumFolderId,
  rawValue,
}) => {
  if (!rosterIdentity) {
    return null;
  }

  const link = getRosterIdentityLink({
    rosterIdentity,
    albumId,
    albumFolderId,
  });
  const gradeValue = link?.grade || rosterIdentity?.grade;

  if (rawValue) {
    return gradeValue;
  }

  const processedGrade = IDENTITY_SCHOOL_GRADES[gradeValue];
  return processedGrade
    ? processedGrade.replace(/grade/gi, '').trim()
    : processedGrade;
};

/**
 * Returns the class name for a roster identity from link records.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.rosterIdentity - The roster identity to get the class name for.
 * @param {string} params.albumId - The album ID to match against (optional).
 * @param {string} params.albumFolderId - The album folder ID to match against (optional).
 * @returns {string|null} - The class name or null if not available.
 */
const getRosterIdentityClassName = ({
  rosterIdentity,
  albumId,
  albumFolderId,
}) => {
  if (!rosterIdentity) {
    return null;
  }

  const link = getRosterIdentityLink({
    rosterIdentity,
    albumId,
    albumFolderId,
  });
  return link?.class_name || rosterIdentity?.class_name;
};

/**
 * Returns the department for a roster identity from link records.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.rosterIdentity - The roster identity to get the department for.
 * @param {string} params.albumId - The album ID to match against (optional).
 * @param {string} params.albumFolderId - The album folder ID to match against (optional).
 * @returns {string|null} - The department or null if not available.
 */
const getRosterIdentityDepartment = ({
  rosterIdentity,
  albumId,
  albumFolderId,
}) => {
  if (!rosterIdentity) {
    return null;
  }

  const link = getRosterIdentityLink({
    rosterIdentity,
    albumId,
    albumFolderId,
  });
  return link?.department || rosterIdentity?.department;
};

/**
 * Returns the period for a roster identity from link records.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.rosterIdentity - The roster identity to get the period for.
 * @param {string} params.albumId - The album ID to match against (optional).
 * @param {string} params.albumFolderId - The album folder ID to match against (optional).
 * @returns {string|null} - The period or null if not available.
 */
const getRosterIdentityPeriod = ({
  rosterIdentity,
  albumId,
  albumFolderId,
}) => {
  if (!rosterIdentity) {
    return null;
  }

  const link = getRosterIdentityLink({
    rosterIdentity,
    albumId,
    albumFolderId,
  });
  return link?.period;
};

/**
 * Returns the track for a roster identity from link records.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.rosterIdentity - The roster identity to get the track for.
 * @param {string} params.albumId - The album ID to match against (optional).
 * @param {string} params.albumFolderId - The album folder ID to match against (optional).
 * @returns {string|null} - The track or null if not available.
 */
const getRosterIdentityTrack = ({ rosterIdentity, albumId, albumFolderId }) => {
  if (!rosterIdentity) {
    return null;
  }

  const link = getRosterIdentityLink({
    rosterIdentity,
    albumId,
    albumFolderId,
  });
  return link?.track;
};

/**
 * Returns the title for a roster identity from link records.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.rosterIdentity - The roster identity to get the title for.
 * @param {string} params.albumId - The album ID to match against (optional).
 * @param {string} params.albumFolderId - The album folder ID to match against (optional).
 * @returns {string|null} - The title or null if not available.
 */
const getRosterIdentityTitle = ({ rosterIdentity, albumId, albumFolderId }) => {
  if (!rosterIdentity) {
    return null;
  }

  const link = getRosterIdentityLink({
    rosterIdentity,
    albumId,
    albumFolderId,
  });
  return link?.title || rosterIdentity?.title;
};

/**
 * Returns the organization ref ID for a roster identity from link records.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.rosterIdentity - The roster identity to get the organization ref ID for.
 * @param {string} params.albumId - The album ID to match against (optional).
 * @param {string} params.albumFolderId - The album folder ID to match against (optional).
 * @returns {string|null} - The organization ref ID or null if not available.
 */
const getRosterIdentityOrganizationRefId = ({
  rosterIdentity,
  albumId,
  albumFolderId,
}) => {
  if (!rosterIdentity) {
    return null;
  }

  const link = getRosterIdentityLink({
    rosterIdentity,
    albumId,
    albumFolderId,
  });
  return link?.ref_id || rosterIdentity?.organization_ref_id;
};

export {
  isPrimaryIdentity,
  filteredIdentityData,
  identityHasSelfie,
  getAlbumSelectedIdentitiesIds,
  getIdentityJoinedAlbumIds,
  isIdentityJoinedToAlbum,
  isIdentityPurchasedOnAlbum,
  getIdentityName,
  getIdentityLastName,
  getIdentityFullName,
  identitiesFilteredOnAccess,
  identityJoinedToRootAlbumFolder,
  sortByIdentityRelationship,
  requiresValidPhoneToCompleteCheckout,
  getInitialActiveIdentityForPpdPurchase,
  identityHasAlbumMatches,
  getIdentityNameHeuristic,
  getAccountIdentityNamesArray,
  isRosterIdentity,
  getIdentityAccounts,
  getJerseyInfo,
  isClonedIdentity,
  getIdentityReferences,
  getRosterIdentitySelfiePhoto,
  getRosterIdentityAddress,
  getRosterIdentityMainLinkedIdentity,
  getRosterIdentityLink,
  getRosterIdentityTeacher,
  getRosterIdentityGrade,
  getRosterIdentityClassName,
  getRosterIdentityDepartment,
  getRosterIdentityPeriod,
  getRosterIdentityTrack,
  getRosterIdentityTitle,
  getRosterIdentityOrganizationRefId,
};
